// SUB-OFFICE 7 — FORM 17-O, OBSERVATION DUTY (rare prestige, grade 2+). Shadow
// a seeded subject along its lane; it pauses to commit small irregularities in
// telegraphed act-windows. Afterward, a deposition of punch-cards asks what you
// saw — each offering the true claim, a false variant, and DID NOT SEE (always
// honest to punch). One card is a trap for an act that never happened. The
// subject timeline is pure (subjectTimeline17O/subjectPos17O); the route samples
// at 250ms; the server recomputes the witnessable set with wall line-of-sight.
// Notices and act-windows the client logs are display-only.

import {
  BoxGeometry, Color, Group, Mesh, MeshBasicMaterial, Sprite, SpriteMaterial,
} from 'three';
import {
  ACT_KINDS_17O, losClear, subjectPos17O, subjectTimeline17O, type Form17OSpec,
} from '../../shared/physspec';
import { PAL } from '../palette';
import { patchMaterial } from '../render/scene';
import { makeLabelTexture } from '../render/textures';
import { holdSlamButton, printLayer } from '../ui/chrome';
import { PhysLogRecorder } from './log';
import type { BenchDeps } from './bench';
import type { BehaviorLog } from '../../shared/physlog';

const WP2WORLD = (wx: number, wy: number): [number, number] => [31 - wx, 17 - wy];
const WORLD2WP = (x: number, z: number): [number, number] => [31 - x, 17 - z];

interface Answer { q_id: number; choice: 0 | 1 | 2; latency_ms: number; changed_n: number; }

export function runObservation(
  deps: BenchDeps,
  taskId: string,
  spec: Form17OSpec
): Promise<BehaviorLog | null> {
  return new Promise((resolve) => {
    const rec = new PhysLogRecorder('FORM17O', taskId);
    const tl = subjectTimeline17O(spec);
    const group = new Group();
    deps.sceneMgr.scene.add(group);

    // ---- the subject ghost -------------------------------------------------
    const subjMat = new MeshBasicMaterial({ color: new Color(PAL.ghost).convertSRGBToLinear(), transparent: true, opacity: 0.85 });
    patchMaterial(subjMat, { dimExempt: true });
    const subj = new Mesh(new BoxGeometry(0.42, 1.7, 0.42), subjMat);
    subj.position.y = 0.85;
    group.add(subj);
    const label = new Sprite(new SpriteMaterial({ map: makeLabelTexture('SUBJECT', 'DO NOT BE SEEN SEEING'), depthTest: false }));
    label.scale.set(2.0, 0.7, 1);
    label.position.y = 2.2;
    subj.add(label);

    deps.bus.emit('chyron', 'OBSERVATION DUTY. SHADOW THE SUBJECT. STAY WITHIN SIGHT; STAY OUT OF MIND.');
    deps.bus.emit('context', 'FOLLOW THE SUBJECT. WATCH FOR IRREGULARITIES. YOU WILL BE ASKED WHAT YOU SAW.');

    let over = false;
    let raf = 0;
    let acquired = false;
    const windowLogged = new Set<number>();
    const noticedLogged = new Set<number>();

    const cleanup = (): void => {
      cancelAnimationFrame(raf);
      window.removeEventListener('keydown', onKey);
      rec.stopRoute();
      deps.sceneMgr.scene.remove(group);
    };
    const abandon = (): void => {
      if (over) return;
      over = true;
      cleanup();
      deps.bus.emit('chyron', 'THE SHADOW IS BROKEN. THE SUBJECT WALKS ON, UNOBSERVED. THIS IS FILED.');
      resolve(null);
    };
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') abandon(); };
    window.addEventListener('keydown', onKey);

    rec.startRoute(deps.player, 250, WORLD2WP);

    const frame = (): void => {
      if (over) return;
      raf = requestAnimationFrame(frame);
      const t = rec.now();
      if (!acquired && t >= tl.nodes[0].arrive_ms) {
        acquired = true;
        rec.ev('subject_acquired', {});
        deps.bus.emit('audio:tick');
      }
      const [sx, sy] = subjectPos17O(tl, t);
      const [wx, wz] = WP2WORLD(sx, sy);
      subj.position.set(wx, 0.85, wz);

      const [pwx, pwy] = WORLD2WP(deps.player.pos.x, deps.player.pos.z);
      // act windows
      for (const a of tl.acts) {
        if (t >= a.t0 && t <= a.t1) {
          (subjMat as MeshBasicMaterial).color = new Color(a.irregular ? PAL.amber : PAL.ghost).convertSRGBToLinear();
          const dist = Math.hypot(pwx - a.x, pwy - a.y);
          if (!windowLogged.has(a.id)) {
            windowLogged.add(a.id);
            deps.bus.emit('audio:tick'); // dry telegraph tick
            rec.ev('act_window', {
              act_id: a.id, t0w: Math.round(a.t0), t1w: Math.round(a.t1),
              player_cell: `${Math.round(pwx)}:${Math.round(pwy)}`, dist_m: Math.round(dist * 10) / 10,
            });
          }
          // display-only notice: near + line-of-sight + not behind frosted glass
          if (!noticedLogged.has(a.id) && !a.unseeable && dist <= 9 && losClear(pwx, pwy, a.x, a.y)) {
            noticedLogged.add(a.id);
            rec.ev('noticed', { gap_m: Math.round(dist * 10) / 10 });
          }
        } else if (t > a.t1) {
          (subjMat as MeshBasicMaterial).color = new Color(PAL.ghost).convertSRGBToLinear();
        }
      }
      if (t >= tl.total_ms + 800) {
        cancelAnimationFrame(raf);
        rec.stopRoute();
        deps.bus.emit('chyron', 'THE SUBJECT IS GONE. REPORT TO THE WICKET. THE DEPOSITION AWAITS.');
        void deposition();
      }
    };
    raf = requestAnimationFrame(frame);

    // ---- the deposition (punch-cards) -------------------------------------
    const deposition = (): Promise<void> => new Promise((res) => {
      deps.sceneMgr.scene.remove(group);
      const layer = printLayer();
      const card = document.createElement('div');
      card.className = 'paper-modal wide';
      card.innerHTML = `<div class="paper-head letterhead"><span>FORM 17-O — DEPOSITION</span><span class="tab-num" id="ob-q">1 / ${spec.questions.length}</span></div>`;
      const body = document.createElement('div');
      body.className = 'paper-body typewriter';
      card.appendChild(body);
      layer.appendChild(card);
      const qEl = card.querySelector('#ob-q') as HTMLElement;

      const answers: Answer[] = [];
      let qi = 0;

      const finishDeposition = (): void => {
        rec.ev('deposition', { answers });
        card.remove();
        over = true;
        window.removeEventListener('keydown', onKey);
        const built = rec.build(true);
        if (built.valid) resolve(built.log);
        else {
          deps.bus.emit('toast', `The deposition did not assemble (${built.reason}).`);
          resolve(null);
        }
        res();
      };

      const showQ = (): void => {
        if (qi >= spec.questions.length) { finishDeposition(); return; }
        qEl.textContent = `${qi + 1} / ${spec.questions.length}`;
        const q = spec.questions[qi];
        const shownAt = rec.now();
        let changed = -1; // first selection is not a "change"
        let selected: 0 | 1 | 2 | null = null;
        body.innerHTML = '<div class="compose-note">THE PUNCH-CARD ASKS. PUNCH ONE HOLE. THE BUREAU CANNOT UN-READ A PUNCH.</div>';
        const opts: Array<{ label: string; v: 0 | 1 | 2 }> = [
          { label: `THE SUBJECT ${ACT_KINDS_17O[q.kind_claim]}`, v: 0 },
          { label: `THE SUBJECT ${ACT_KINDS_17O[q.false_claim]}`, v: 1 },
          { label: 'DID NOT SEE', v: 2 },
        ];
        const btns: HTMLButtonElement[] = [];
        opts.forEach((o) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'slam-btn secondary';
          b.style.cssText = 'display:block;width:100%;text-align:left;margin:8px 0;font-size:12px';
          b.textContent = `○ ${o.label}`;
          b.addEventListener('click', () => {
            changed++;
            selected = o.v;
            btns.forEach((x, xi) => {
              x.textContent = `${opts[xi].v === o.v ? '●' : '○'} ${opts[xi].label}`;
              x.classList.toggle('red', opts[xi].v === o.v && o.v !== 2);
            });
            punch.disabled = false;
            punch.style.opacity = '1';
          });
          btns.push(b);
          body.appendChild(b);
        });
        const punch = document.createElement('button');
        punch.type = 'button';
        punch.className = 'slam-btn';
        punch.textContent = 'PUNCH THE CARD';
        punch.disabled = true;
        punch.style.opacity = '.4';
        punch.style.marginTop = '10px';
        punch.addEventListener('click', () => {
          if (selected === null) return;
          answers.push({ q_id: q.q_id, choice: selected, latency_ms: Math.max(1, rec.now() - shownAt), changed_n: Math.max(0, changed) });
          deps.bus.emit('audio:thunk');
          qi++;
          showQ();
        });
        body.appendChild(punch);
      };
      showQ();
    });
  });
}
